import {ChangeDetectionStrategy, Component, Injectable, input, Signal} from "@angular/core";
import {AgendaelementId, MøteId} from "felles-ng2/tjenester/ressurser/generertNg2HttpKlient";
import {initDebugger} from "felles-ng2/støttefunksjoner/debug";
import {combineLatest, interval, Observable, startWith, switchMap} from "rxjs";
import {toObservable, toSignal} from "@angular/core/rxjs-interop";
import {Temporal} from "temporal-polyfill";
import {NgClass} from "@angular/common";
import {MatTooltip} from "@angular/material/tooltip";
import {TranslateModule} from "@ngx-translate/core";
import {Sakstidspunkt, Sakstidspunkter, SakstidspunkterInformasjon} from "felles-ng2/tjenester/Sakstidspunkter";
import {Klokke} from "felles-ng2/tjenester/Klokke";
import {durationTilMinutter, erZonedDateTimeTidligereEnn} from "felles-ng2/støttefunksjoner/temporal";
import {map} from "rxjs/operators";
import {AgendaelementModellDLM, SakDatatjeneste} from "felles-ng2/tjenester/datalager/SakDatatjeneste";
import {erTomListe} from "felles-ng2/støttefunksjoner/lister";
import {ZoneddatetimeSomPlainTimePipe} from "felles-ng2/pipes/ZonedDateTimeSomPlainTimePipe";
import {FormaterPlainTimePipe} from "felles-ng2/pipes/FormaterPlainTimePipe";
import Duration = Temporal.Duration;

const debug = initDebugger('SakslisteProgresjonKomponent');

/**
 * Horisontal progresjonslinje som viser hvor langt man har kommet i et pågående møte
 */
@Component({
    selector: 'saksliste-progresjon',
    templateUrl: 'saksliste-progresjon.html',
    styleUrl: 'saksliste-progresjon.scss',
    host: {
        'class': 'ikke-print',
        '[class.skjult]': '!progresjon().vises'
    },
    imports: [
        NgClass,
        MatTooltip,
        TranslateModule,
        ZoneddatetimeSomPlainTimePipe,
        FormaterPlainTimePipe
    ],
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class SakslisteProgresjonKomponent {
    static ajsSelector = 'sakslisteProgresjon';

    id = input.required<MøteId>();

    progresjon: Signal<Progresjonsinformasjon>;

    constructor(private avhengigheter: SakslisteProgresjonKomponentAvhengigheter, private sakstidspunkter: Sakstidspunkter, private klokke: Klokke) {
        const aktivSakSomObserverbar = toObservable(this.avhengigheter.aktivSakSomSignal());
        this.progresjon = toSignal(toObservable(this.id).pipe(
          //lever verdier når 'møte.saker endres' eller 'beregnede sakstidspunkter endres' eller 'når det har gått 10 sekunder'
          switchMap(id => combineLatest([this.avhengigheter.saker(id), this.sakstidspunkterForMøte(id), aktivSakSomObserverbar, this.hvertNSekund(10)])),
          map(([saker, sakstidspunkter, aktivSak]) => this.beregnSaksprogresjoner(saker, sakstidspunkter, aktivSak))
        ), {initialValue: tomProgresjonsinformasjon()})
    }

    //private hjelpemetoder

    private beregnSaksprogresjoner(saker: AgendaelementModellDLM[], sakstidspunkter: SakstidspunkterInformasjon, aktivSak: AgendaelementId | null): Progresjonsinformasjon {
        const nå = this.klokke.nåSomZonedDateTime();
        //ved flerdagsmøter må alle saker ha varighet satt for at vi skal kunne beregne
        if (sakstidspunkter.flerdagsmoete && !sakstidspunkter.alleHarVarighetSatt) return tomProgresjonsinformasjon();
        if (erTomListe(saker) || !sakstidspunkter.harSakerPåDag(nå)) return tomProgresjonsinformasjon(); //har ikke nok info til å gjøre beregninger

        //finn sakene som behandles 'i dag'
        const sakerIDagVarighet = sakstidspunkter.sakerPåDagVarighet(nå);
        const saksprogresjoner = sakstidspunkter.sakerPåDag(nå)
          .map(sakstidspunkt => ({
              sakstidspunkt: sakstidspunkt,
              sak: saker.find(sak => sak.id == sakstidspunkt.sakId)!,
              relativBredde: this.andelAvMøtetsVarighet(sakstidspunkt.varighet!, sakerIDagVarighet),
              erBehandlet: erZonedDateTimeTidligereEnn(sakstidspunkt.slutt!, nå),
              erIkkeBehandlet: erZonedDateTimeTidligereEnn(nå, sakstidspunkt.slutt!),
              erUnderBehandling: erZonedDateTimeTidligereEnn(sakstidspunkt.start!, nå) && erZonedDateTimeTidligereEnn(nå, sakstidspunkt.slutt!),
              erAktivSak: sakstidspunkt.sakId == aktivSak
          }));
        const saksvarighetSum = sakstidspunkter.sakstidspunkterSomListe().reduce((sum, sak) => sum.add(sak.varighet!), new Duration(0));
        const antallMinutterUdisponertTid = sakstidspunkter.flerdagsmoete ? new Duration(0) : sakerIDagVarighet.subtract(saksvarighetSum);

        return {
            vises: true,
            relativProgresjon: this.andelAvMøtetsVarighet(sakstidspunkter.sakerPåDagStart(nå).until(nå), sakerIDagVarighet),
            saker: saksprogresjoner,
            udisponertTidMinutter: durationTilMinutter(antallMinutterUdisponertTid),
            udisponertTidRelativBredde: this.andelAvMøtetsVarighet(antallMinutterUdisponertTid, sakerIDagVarighet)
        };
    }

    /** Leverer verdi hvert N-te sekund */
    private hvertNSekund(intervalISekunder: number): Observable<any> {
        return interval(1000 * intervalISekunder).pipe(startWith(0));
    }

    private sakstidspunkterForMøte(id: string): Observable<SakstidspunkterInformasjon> {
        return this.sakstidspunkter.forMøte(id).tidspunkterSomObserverbar();
    }

    private andelAvMøtetsVarighet(varighet: Duration, møtetsVarighet: Duration): string {
        return this.somProsent((durationTilMinutter(varighet) / durationTilMinutter(møtetsVarighet)));
    }

    /** returnerer angitt prosent (feks 0.612345) som teksten '61.23%' */
    private somProsent(prosent: number): string {
        const prosentSomStreng = rundAvTilNDesimaler(prosent * 100, 2);
        return `${prosentSomStreng}%`
    }
}


function rundAvTilNDesimaler(tall: number, antallDesimaler: number): number {
    var faktor = 10 * antallDesimaler;
    return Math.floor(tall * faktor) / faktor;
}

@Injectable()
export class SakslisteProgresjonKomponentAvhengigheter {

    constructor(private sakDatatjeneste: SakDatatjeneste) {
    }

    saker(id: MøteId): Observable<AgendaelementModellDLM[]> {
        return this.sakDatatjeneste.finnTilknyttetMøteFraLokallager(id);
    }

    aktivSakSomSignal(): Signal<AgendaelementId | null> {
        return this.sakDatatjeneste.aktivSakIdSomSignal;
    }
}

interface Progresjonsinformasjon {
    vises: boolean;
    relativProgresjon: string;
    saker: SakProgresjon[];
    udisponertTidMinutter: number;
    udisponertTidRelativBredde: string;
}

interface SakProgresjon {
    sakstidspunkt: Sakstidspunkt;
    sak: AgendaelementModellDLM;
    relativBredde: string;
    erBehandlet: boolean;
    erIkkeBehandlet: boolean;
    erUnderBehandling: boolean;
    erAktivSak: boolean;
}

function tomProgresjonsinformasjon(): Progresjonsinformasjon {
    return {vises: false, relativProgresjon: '', saker: [], udisponertTidMinutter: 0, udisponertTidRelativBredde: ''}
}
